<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { Plus, Minus, RotateCcw } from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import { GEO_MAP_FILLS, POPULATION_COLOR_PALETTES } from "@/lib/chartPalettes";

export interface GeoZone {
  code: string;
  respondents: number;
  metricBase: number;
  value: number | null;
}

interface Bounds {
  lonMin: number;
  lonMax: number;
  latMin: number;
  latMax: number;
  latRef: number;
}

interface CountryMap {
  load: () => Promise<{ default?: FeatureCollection } & Partial<FeatureCollection>>;
  bounds: Bounds;
  // Zones far from the mainland, each drawn in its own box along the bottom of the map.
  insets: string[];
  attribution: string;
}

interface Feature {
  properties: { code: string; nom: string };
  geometry: { type: string; coordinates: unknown };
}

interface FeatureCollection {
  features: Feature[];
}

const COUNTRY_MAPS: Record<string, CountryMap> = {
  FR: {
    load: () => import("@/assets/geo/france-departements.json"),
    bounds: { lonMin: -5.3, lonMax: 9.8, latMin: 41.2, latMax: 51.2, latRef: 46.2 },
    insets: ["971", "972", "973", "974", "976"],
    attribution: "IGN / INSEE",
  },
};

const props = withDefaults(
  defineProps<{
    zones: GeoZone[];
    country?: string;
    hasMetric?: boolean;
    metricKind?: "average" | "share" | null;
    color?: string;
    minBase?: number;
    zoomable?: boolean;
  }>(),
  { country: "FR", hasMetric: false, metricKind: null, color: POPULATION_COLOR_PALETTES.blue[500], minBase: 5, zoomable: true },
);

const WIDTH = 620;
const HEIGHT = 600;

const countryMap = computed(() => COUNTRY_MAPS[props.country] || COUNTRY_MAPS.FR);
const features = ref<Feature[]>([]);

watch(
  countryMap,
  async (map) => {
    const geo = await map.load();
    features.value = (geo.default || geo).features || [];
  },
  { immediate: true },
);

type Projector = (point: number[]) => string;

// Equirectangular projection of `bounds` into a frame, longitudes shrunk by the cosine of latRef.
function projector(bounds: Bounds, frame: { x: number; y: number; width: number; height: number }): Projector {
  const lonScale = Math.cos((bounds.latRef * Math.PI) / 180);
  const scale = Math.min(frame.width / ((bounds.lonMax - bounds.lonMin) * lonScale), frame.height / (bounds.latMax - bounds.latMin));
  const offsetX = frame.x + (frame.width - (bounds.lonMax - bounds.lonMin) * lonScale * scale) / 2;
  const offsetY = frame.y + (frame.height - (bounds.latMax - bounds.latMin) * scale) / 2;
  return ([lon, lat]) => {
    const x = offsetX + (lon - bounds.lonMin) * lonScale * scale;
    const y = offsetY + (bounds.latMax - lat) * scale;
    return `${Math.round(x * 10) / 10},${Math.round(y * 10) / 10}`;
  };
}

function featureRings(feature: Feature): number[][][] {
  const { type, coordinates } = feature.geometry;
  if (type === "Polygon") return coordinates as number[][][];
  return (coordinates as number[][][][]).flat();
}

function featureBounds(feature: Feature): Bounds {
  const points = featureRings(feature).flat();
  const lons = points.map(([lon]) => lon);
  const lats = points.map(([, lat]) => lat);
  const latMin = Math.min(...lats);
  const latMax = Math.max(...lats);
  return { lonMin: Math.min(...lons), lonMax: Math.max(...lons), latMin, latMax, latRef: (latMin + latMax) / 2 };
}

function featurePath(feature: Feature, project: Projector): string {
  return featureRings(feature)
    .map((ring) => `M${ring.map(project).join("L")}Z`)
    .join("");
}

const INSET = { size: 60, gap: 8, bottom: 4, padding: 6 };

const insets = computed(() =>
  countryMap.value.insets.flatMap((code, index) => {
    const feature = features.value.find((f) => f.properties.code === code);
    if (!feature) return [];
    const box = { x: INSET.gap + index * (INSET.size + INSET.gap), y: HEIGHT - INSET.size - INSET.bottom, size: INSET.size };
    const inner = INSET.size - 2 * INSET.padding;
    const project = projector(featureBounds(feature), { x: box.x + INSET.padding, y: box.y + INSET.padding, width: inner, height: inner });
    return [{ code, box, path: featurePath(feature, project) }];
  }),
);

const paths = computed(() => {
  const project = projector(countryMap.value.bounds, { x: 0, y: 0, width: WIDTH, height: HEIGHT });
  const mainland = features.value.filter((feature) => !countryMap.value.insets.includes(feature.properties.code));
  const insetPaths = insets.value.map((inset) => [inset.code, inset.path] as const);
  return new Map([...mainland.map((feature) => [feature.properties.code, featurePath(feature, project)] as const), ...insetPaths]);
});

const zonesByCode = computed(() => new Map(props.zones.map((zone) => [zone.code, zone])));
const belowBase = (zone: GeoZone) => props.hasMetric && zone.metricBase < props.minBase;
const valueOf = (zone: GeoZone) => (props.hasMetric ? zone.value : zone.respondents);

const domain = computed(() => {
  const values = props.zones
    .filter((zone) => !belowBase(zone))
    .map(valueOf)
    .filter((v): v is number => v != null);
  if (!values.length) return { min: 0, max: 0 };
  return { min: Math.min(...values), max: Math.max(...values) };
});

const hasLegend = computed(() => props.zones.some((zone) => !belowBase(zone) && valueOf(zone) != null));

const hexToRgb = (hex: string) => [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)].map((part) => parseInt(part, 16));
const rampTo = computed(() => hexToRgb(props.color));
const rampFrom = computed(() => rampTo.value.map((channel) => Math.round(255 - (255 - channel) * 0.12)));
const rampCss = (rgb: number[]) => `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;

const EMPTY_FILL = GEO_MAP_FILLS.empty;
const LOW_BASE_FILL = GEO_MAP_FILLS.lowBase;

function colorFor(code: string): string {
  const zone = zonesByCode.value.get(code);
  if (!zone) return EMPTY_FILL;
  if (belowBase(zone)) return LOW_BASE_FILL;
  const value = valueOf(zone);
  if (value == null) return EMPTY_FILL;
  const { min, max } = domain.value;
  const ratio = max > min ? (value - min) / (max - min) : 1;
  return rampCss(rampFrom.value.map((from, idx) => Math.round(from + (rampTo.value[idx] - from) * ratio)));
}

function formatBound(value: number) {
  const rounded = Math.round(value * 10) / 10;
  return props.metricKind === "share" ? `${rounded}%` : rounded;
}

const legendLabel = computed(() => {
  if (props.metricKind === "share") return "share";
  if (props.metricKind === "average") return "average";
  return "respondents";
});

const MAX_ZOOM = 8;
const zoom = ref(1);
const center = ref({ x: WIDTH / 2, y: HEIGHT / 2 });

const viewBox = computed(() => {
  const w = WIDTH / zoom.value;
  const h = HEIGHT / zoom.value;
  const x = Math.min(Math.max(center.value.x - w / 2, 0), WIDTH - w);
  const y = Math.min(Math.max(center.value.y - h / 2, 0), HEIGHT - h);
  return `${x} ${y} ${w} ${h}`;
});

function zoomBy(factor: number) {
  zoom.value = Math.min(MAX_ZOOM, Math.max(1, zoom.value * factor));
  if (zoom.value === 1) center.value = { x: WIDTH / 2, y: HEIGHT / 2 };
}

function resetZoom() {
  zoom.value = 1;
  center.value = { x: WIDTH / 2, y: HEIGHT / 2 };
}

const svgEl = ref<SVGSVGElement | null>(null);
let dragging = false;
let last = { x: 0, y: 0 };

function onPointerDown(event: PointerEvent) {
  dragging = true;
  last = { x: event.clientX, y: event.clientY };
  (event.currentTarget as Element).setPointerCapture(event.pointerId);
}

function onPointerMove(event: PointerEvent) {
  trackTooltip(event);
  if (!dragging || zoom.value === 1) return;
  const clientWidth = svgEl.value?.clientWidth || WIDTH;
  const unitsPerPixel = WIDTH / zoom.value / clientWidth;
  center.value = {
    x: center.value.x - (event.clientX - last.x) * unitsPerPixel,
    y: center.value.y - (event.clientY - last.y) * unitsPerPixel,
  };
  last = { x: event.clientX, y: event.clientY };
}

function onPointerUp() {
  dragging = false;
}

interface Hovered {
  name: string;
  respondents: number;
  metricBase: number;
  value: number | null;
  lowBase: boolean;
}

const hovered = ref<Hovered | null>(null);
const hoverPos = ref({ x: 0, y: 0 });
const wrapperEl = ref<HTMLElement | null>(null);

function trackTooltip(event: PointerEvent) {
  const rect = wrapperEl.value?.getBoundingClientRect();
  if (!rect) return;
  hoverPos.value = { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

function showTooltip(feature: Feature) {
  const zone = zonesByCode.value.get(feature.properties.code);
  hovered.value = {
    name: `${feature.properties.nom} (${feature.properties.code})`,
    respondents: zone?.respondents || 0,
    metricBase: zone?.metricBase || 0,
    value: props.hasMetric ? (zone?.value ?? null) : null,
    lowBase: zone ? belowBase(zone) : false,
  };
}

const hideTooltip = () => {
  hovered.value = null;
};

const unmappedZones = computed(() => {
  const drawn = new Set(features.value.map((feature) => feature.properties.code));
  return props.zones.filter((zone) => !drawn.has(zone.code));
});
</script>

<template>
  <div class="flex flex-col items-center gap-3">
    <div ref="wrapperEl" class="relative w-full max-w-lg">
      <svg
        ref="svgEl"
        :viewBox="viewBox"
        class="w-full touch-none"
        :class="zoom > 1 ? 'cursor-grab active:cursor-grabbing' : ''"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointerleave="
          onPointerUp();
          hideTooltip();
        ">
        <path
          v-for="feature in features"
          :key="feature.properties.code"
          :d="paths.get(feature.properties.code)"
          :fill="colorFor(feature.properties.code)"
          :stroke-width="0.8 / zoom"
          class="stroke-background transition-opacity hover:opacity-75"
          @pointerenter="showTooltip(feature)" />
        <g v-for="inset in insets" :key="`inset-${inset.code}`" class="pointer-events-none">
          <rect
            :x="inset.box.x"
            :y="inset.box.y"
            :width="inset.box.size"
            :height="inset.box.size"
            rx="2"
            fill="none"
            :stroke-width="0.8 / zoom"
            class="stroke-border" />
          <text :x="inset.box.x + 3" :y="inset.box.y + 9" class="fill-muted-foreground" font-size="7">{{ inset.code }}</text>
        </g>
      </svg>

      <div
        v-if="hovered"
        class="pointer-events-none absolute z-10 rounded-md border bg-background px-2.5 py-1.5 text-xs shadow-md"
        :style="{ left: `${hoverPos.x + 12}px`, top: `${hoverPos.y + 12}px` }">
        <div class="font-medium">{{ hovered.name }}</div>
        <div class="tabular-nums text-muted-foreground">{{ hovered.respondents }} respondents</div>
        <div v-if="hasMetric" class="tabular-nums" :class="hovered.lowBase ? 'text-muted-foreground' : 'font-medium'">
          <template v-if="hovered.lowBase">base too small ({{ hovered.metricBase }} answered, &lt; {{ minBase }})</template>
          <template v-else-if="hovered.value == null">no data</template>
          <template v-else-if="metricKind === 'share'">{{ hovered.value }}% picked it ({{ hovered.metricBase }} answered)</template>
          <template v-else>avg {{ hovered.value }} ({{ hovered.metricBase }} answered)</template>
        </div>
      </div>

      <div v-if="zoomable" class="card-toolbar absolute right-2 top-2 flex flex-col gap-1">
        <Button variant="outline" size="icon-sm" title="Zoom in" aria-label="Zoom in" @click="zoomBy(1.5)">
          <Plus />
        </Button>
        <Button variant="outline" size="icon-sm" title="Zoom out" aria-label="Zoom out" @click="zoomBy(1 / 1.5)">
          <Minus />
        </Button>
        <Button v-if="zoom > 1" variant="outline" size="icon-sm" title="Reset zoom" aria-label="Reset zoom" @click="resetZoom">
          <RotateCcw class="size-3.5" />
        </Button>
      </div>
    </div>

    <div v-if="hasLegend" class="flex items-center gap-2 text-xs text-muted-foreground">
      <span>{{ formatBound(domain.min) }}</span>
      <span class="h-2 w-32 rounded-full" :style="{ background: `linear-gradient(to right, ${rampCss(rampFrom)}, ${rampCss(rampTo)})` }" />
      <span>{{ formatBound(domain.max) }}</span>
      <span class="ml-1">{{ legendLabel }}</span>
      <span v-if="hasMetric" class="ml-2 flex items-center gap-1">
        <span class="inline-block h-2.5 w-2.5 rounded-sm" :style="{ backgroundColor: LOW_BASE_FILL }" />
        n &lt; {{ minBase }}
      </span>
    </div>

    <div v-if="unmappedZones.length" class="flex flex-wrap items-center justify-center gap-1.5 text-xs text-muted-foreground">
      <span>Not on the map:</span>
      <span v-for="zone in unmappedZones" :key="zone.code" class="rounded-full border px-2 py-0.5 tabular-nums">
        {{ zone.code }} · {{ zone.respondents
        }}<template v-if="hasMetric && zone.value != null"> · {{ zone.value }}{{ metricKind === "share" ? "%" : "" }}</template>
      </span>
    </div>

    <p class="text-3xs text-muted-foreground">Map data: {{ countryMap.attribution }}</p>
  </div>
</template>
